import { create } from 'zustand';

/**
 * Global scope filter for the People section (Users / Practitioners / Clients).
 * Persisted to localStorage so the admin's chosen scope survives reloads.
 *
 * Notes on the BE contract:
 *  - `GET /users` accepts `organizationId` only — no `locationId`. The Users
 *    list screen reads `organizationId` from this store and ignores
 *    `locationId`. The PeopleScopeSwitcher hides the location selector
 *    entirely on Users routes so the surface matches the BE.
 *  - `GET /practitioners` and `GET /clients` accept both.
 */

const STORAGE_KEY = 'elixaa.peopleScope';

interface PeopleScopeState {
  organizationId: string | null;
  organizationName: string | null;
  locationId: string | null;
  locationName: string | null;
}

interface PeopleScopeActions {
  setOrganization: (org: { id: string; name: string } | null) => void;
  setLocation: (loc: { id: string; name: string } | null) => void;
  reset: () => void;
}

type PeopleScopeStore = PeopleScopeState & PeopleScopeActions;

const initial: PeopleScopeState = {
  organizationId: null,
  organizationName: null,
  locationId: null,
  locationName: null,
};

function readFromStorage(): PeopleScopeState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initial;
    const parsed = JSON.parse(raw) as Partial<PeopleScopeState>;
    return {
      organizationId: parsed.organizationId ?? null,
      organizationName: parsed.organizationName ?? null,
      locationId: parsed.locationId ?? null,
      locationName: parsed.locationName ?? null,
    };
  } catch {
    return initial;
  }
}

function writeToStorage(state: PeopleScopeState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable in private/strict modes — degrade silently.
  }
}

/**
 * Remove the persisted scope WITHOUT mutating the in-memory store.
 *
 * Called from the auth teardown (`authStore.clearBothStorages`) so one user's
 * org/location selection can't leak into the next user's session on a shared
 * browser — a stale org makes every scope-reading screen send an
 * organizationId the new user has no access to, which the BE rejects with a
 * 404 ("Resource not found") via `checkSameBAFor`.
 *
 * Storage-only by design, mirroring `clearAuthStorage`: every teardown path is
 * followed by `window.location.replace(...)`, and calling `reset()` here would
 * synchronously re-render the still-mounted protected page before unload (the
 * first-paint-flash class of bug documented in authStore.ts). The reload
 * re-runs this module's initializer, and `readFromStorage()` then returns
 * `initial`.
 */
export function clearPeopleScopeStorage(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable in private/strict modes — degrade silently.
  }
}

export const usePeopleScopeStore = create<PeopleScopeStore>((set, get) => ({
  ...readFromStorage(),

  setOrganization: (org) => {
    // Changing the org clears the location — locations are scoped to a single
    // org, so a previous location selection is no longer meaningful.
    const next: PeopleScopeState = {
      organizationId: org?.id ?? null,
      organizationName: org?.name ?? null,
      locationId: null,
      locationName: null,
    };
    writeToStorage(next);
    set(next);
  },

  setLocation: (loc) => {
    const current = get();
    if (!current.organizationId) return;
    const next: PeopleScopeState = {
      ...current,
      locationId: loc?.id ?? null,
      locationName: loc?.name ?? null,
    };
    writeToStorage(next);
    set(next);
  },

  reset: () => {
    writeToStorage(initial);
    set(initial);
  },
}));
